/* eslint-disable @typescript-eslint/no-misused-promises */

import { useRef, useState } from 'react';
import { reportOperationLog } from '@gp/services/global';
import { downloadPDFByNode } from '@gp/utils/common';
import { captureNode } from '@gp/utils/file';
import { handleCaptureNodeClone } from '../v1/utils';
import { ALL_REPORT_OPERATION_LOG } from '@gp/constants/operationLogParams';
import { DOWNLOAD_PNG_TYPE, DOWNLOAD_PDF_TYPE } from '../constants';

import type { IRecord } from '@gp/types/global';
import type { IDPSReportVO } from '@gp/types/dpsReport/common';

const DOWNLOAD_REPORT_KEY_MAPPING: IRecord<string> = {
  [DOWNLOAD_PNG_TYPE]: 'WORKSPACE_DPS_WEEKLY_REPORT_IMG_DOWNLOAD_SUBMIT',
  [DOWNLOAD_PDF_TYPE]: 'WORKSPACE_DPS_WEEKLY_REPORT_PDF_DOWNLOAD_SUBMIT',
};

export default function useDPSReportDownload(title: string, report: IDPSReportVO | null) {
  // DPS报告图片容器节点
  const node = useRef<HTMLDivElement>(null);

  // 下载状态
  const [isDownloading, setIsDownloading] = useState(false);

  const reportDownload = (payload: string) => {
    const downloadReportCfg = ALL_REPORT_OPERATION_LOG[DOWNLOAD_REPORT_KEY_MAPPING[payload]];
    if (downloadReportCfg) {
      // @ts-ignore
      reportOperationLog(downloadReportCfg, 'button');
    }
  };

  const handleDownload = (value: string) => {
    if (!node.current) return;
    if (isDownloading) return;
    // 这里需要提前更新loading为true，避免setTimeout延时期间，下载弹窗已关闭了，但是下载按钮还没进入loading状态的空白阶段
    setIsDownloading(true);
    // 这里使用setTimeout 延时300ms，等页面其他UI刷新完后，再截屏，避免 html2canvas截图时占用性能 导致 页面其他UI卡顿的问题
    setTimeout(async () => {
      try {
        const fileName = title || 'DPS Weekly Newsletter';
        const fileFormat = value || DOWNLOAD_PNG_TYPE;
        if (value === DOWNLOAD_PDF_TYPE) {
          await downloadPDFByNode({
            node: node.current as any,
            fileName,
            html2canvasOptions: {},
            jsPDFOptions: {},
          });
        } else if (value === DOWNLOAD_PNG_TYPE) {
          await captureNode({
            type: fileFormat as any,
            node: node.current as any,
            fileName,
            options: {
              /**
               * 这里是因为在可编辑的dps周报截图的时候，html2canvas这玩意儿读取到node节点的宽度偏小，所以在onclone回调内 强制纠正
               * html2canvas这玩意儿 不知道什么原因 会丢失 width、height、padding这些样式，需要在onclone回调里 再赋值一遍，不然下载下来的东西 跟 页面展示的不一样
               */
              onclone: (document: Document, element: HTMLElement) => {
                if (!node.current || !element) return;
                if (report?.type && ['1', '3'].includes(report?.type)) {
                  handleCaptureNodeClone(node, element);
                }
              },
            },
          });
        }
      } finally {
        setIsDownloading(false);
        reportDownload(value);
      }
    }, 300);
  };

  return { node, isDownloading, onDownload: handleDownload };
}
